import { useEffect, useState } from 'react';
import ArrowLeft from '../../../../../Assets/Arrow left.svg';
import { useDispatch, useSelector } from 'react-redux';
import Toggle from '../../../../../Components/Toggle/Toggle';
import { closeModal, fetchInventoryProducts, setSelectedProduct } from '../../../../../Features/inventoryManagementSlice';
import { showToast } from '../../../../../Components/Toaster/Toaster';
import { updateInventoryVariant, updateProduct } from '../../../../../lib/utils/apiCalls';
import { toast } from 'react-toastify';

function UpdateStockForm() {
    const dispatch = useDispatch();
    const selectedProduct = useSelector((state) => state.inventoryManagement.selectedProduct);

    const [activeSection, setActiveSection] = useState('add');

    const [stockFormState, setStockFormState] = useState([]);

    const handleSectionSelect = (e) => {
        setActiveSection(e.target.id);
    }

    const handleCancel = () => {
        dispatch(setSelectedProduct({}));
    }

    const handleStockInputChange = (e) => {
        const { value, name, id } = e.target;

        if(value < 0) {
            showToast('Stock can\'t be negative.', 'warning');
            return;
        }
        else {
            if(activeSection === 'remove') {
                let selectedProduct = stockFormState.find(item => item.id === id);

                if(selectedProduct.VarientQuantity < value) {
                    if(selectedProduct.VarientQuantity === 0) {
                        showToast('Product already has the least stock.', 'warning');
                        return;
                    }
                    else {
                        showToast(`Max ${selectedProduct.VarientQuantity} items can be removed.`, 'warning');
                        return;
                    }
                }
            }

            setStockFormState(prevVariants => 
                prevVariants.map(variant => 
                    variant.id === id ? { ...variant, [name]: value } : variant
                )
            );
        }
    }

    const handleUpdateStocks = async () => {
        let errors = false;

        stockFormState.forEach(item => {
            if (activeSection === 'remove') {
                if (Number(item.VarientFormInput) > item.VarientQuantity) {
                    showToast(`Input quantity is greater than actual quantity for ${item.VarientName}.`, 'warning');
                    errors = true;
                }
            }
        });

        let count = 0;
        stockFormState.forEach(item => {
            if(!Boolean(item.VarientFormInput)) {
                count++;
            }
        });

        if(count === stockFormState.length) {
            showToast(`Input quantity is required for at least one product.`, 'warning');
            errors = true;
        }

        if (!errors) {
            let updatePromises;

            if (activeSection === 'add') {
                updatePromises = stockFormState
                    .filter(item => item.VarientFormInput !== '')
                    .map(item => {
                        if (item.isVariant) {
                            return updateInventoryVariant(
                                { VarientQuantity: item.VarientQuantity + Number(item.VarientFormInput) },
                                item.id
                            );
                        } else {
                            return updateProduct(
                                { productStock: item.VarientQuantity + Number(item.VarientFormInput) },
                                item.id
                            );
                        }
                    });
            } else {
                updatePromises = stockFormState
                    .filter(item => item.VarientFormInput !== '')
                    .map(item => {
                        if (item.isVariant) {
                            return updateInventoryVariant(
                                { VarientQuantity: item.VarientQuantity - Number(item.VarientFormInput) },
                                item.id
                            );
                        } else {
                            return updateProduct(
                                { productStock: item.VarientQuantity - Number(item.VarientFormInput) },
                                item.id
                            );
                        }
                    });
            }

            const updateAllPromise = Promise.all(updatePromises);

            toast.promise(updateAllPromise, {
                pending: {
                    render() {
                        return 'Updating stocks for selected products...';
                    },
                    className: 'toast-pending',
                },
                success: {
                    render() {
                        dispatch(fetchInventoryProducts());
                        dispatch(closeModal());
                        dispatch(setSelectedProduct({}));
                        return 'Stocks updated successfully.';
                    },
                },
                error: {
                    render() {
                        return 'Failed to update stocks for some or all products. Please try again.';
                    },
                }
            });
        }
    }

    useEffect(() => {
        if (Object.keys(selectedProduct).length > 0) {
            let formArray = [];

            if (selectedProduct.varients?.length > 0) {
                // Normal case: map each variant
                selectedProduct.varients.forEach(varient => {
                    let object = {
                        id: varient.id,
                        VarientName: varient.VarientName || selectedProduct.product.productName,
                        VarientPrice: varient.VarientPrice,
                        VarientMrp: varient.VarientMrp,
                        VarientImgUrl: varient.VarientImgUrl || selectedProduct.product.productImgUrls,
                        VarientQuantity: varient.VarientQuantity === "" ? 0 : varient.VarientQuantity ?? 0,
                        VarientSize: varient.VarientSize || selectedProduct.product.productSpecs?.size,
                        VarientColor: varient.VarientColor || selectedProduct.product.productSpecs?.color,
                        sku: varient.sku,
                        isVariant: true,
                        VarientFormInput: '',
                        category: selectedProduct?.product?.productCategory,
                    };
                    formArray.push(object);
                });
            } else {
                // Fallback: pseudo-variant from main product
                let pseudoVariant = {
                    id: selectedProduct.product.id,
                    VarientName: selectedProduct.product.productName,
                    VarientPrice: selectedProduct.product.productSellingPrice,
                    VarientMrp: selectedProduct.product.productMRP,
                    VarientImgUrl: selectedProduct.product.productImgUrls,
                    VarientQuantity: selectedProduct.product.productStock || 0,
                    VarientSize: selectedProduct.product.productSpecs?.size,
                    VarientColor: selectedProduct.product.productSpecs?.color,
                    sku: selectedProduct.product.productSKU,
                    isVariant: false,
                    VarientFormInput: '',
                    category: selectedProduct?.product?.productCategory,
                };
                formArray.push(pseudoVariant);
            }

            setStockFormState(formArray);
        }
    }, [selectedProduct]);

    return (
        <div className='flex flex-col gap-6 h-[calc(100%-44px)]'>
            <div className="w-full flex items-center">
                <div onClick={handleCancel} className="cursor-pointer p-[10px] flex items-center justify-center">
                    <img className='size-6 text-[#666666]' src={ArrowLeft} alt="" />
                </div>

                <h1 className="w-[calc(100%-24px)] p-[6px] text-[#121212] font-bold leading-8 text-[24px] line-clamp-1">{selectedProduct?.product.productName}</h1>
            </div>

            <Toggle 
                items={{
                    firstItem: 'Add',
                    secondItem: 'Remove',
                    firstItemID: 'add',
                    secondItemID: 'remove', 
                    activeList: activeSection
                }} 
                onClick={handleSectionSelect} 
            />

            <div className={`${selectedProduct?.varients?.length > 4 ? 'h-[calc(100%-138px)]' : 'min-h-fit max-h-[calc(100%-138px)]'} border border-[#EAEAEA] rounded-lg overflow-hidden ${selectedProduct?.varients?.length > 4 && 'shadow-[inset_0_-2px_8px_rgba(0,0,0,0.1)]'}`}>
                <div className="flex items-center bg-[#F5F5F5] uppercase text-[14px] leading-[26px] font-semibold text-[#121212]">
                    <h2 className='w-[64%] px-[10px] py-2'>product name</h2>

                    <h2 className='w-[18%] px-[10px] py-2'>available stocks</h2>

                    <h2 className='w-[18%] px-[10px] py-2'>update stocks</h2>
                </div>

                <div className="h-[calc(100%-42px)] overflow-y-scroll no-scrollbar">
                    {[...stockFormState]
                        .sort((a, b) => {
                            const qtyA = a.VarientQuantity;
                            const qtyB = b.VarientQuantity;
                            return qtyA - qtyB;
                        }).map(variant => {
                        return (
                            <div key={variant.id} className='flex items-center w-full border-t border-[#EAEAEA] py-[6px] select-none'>
                                <div className="w-[64%] flex items-center gap-2 px-[10px]">
                                    <div className="size-16 rounded-lg overflow-hidden">
                                        <img className='size-full object-cover' src={variant?.VarientImgUrl[0] || ''} alt="" />
                                    </div>

                                    <div className="flex flex-col gap-2 w-[calc(100%-64px)]">
                                        <p className='font-semibold text-[18px] leading-[26px] w-full whitespace-nowrap overflow-hidden text-ellipsis text-[#121212]'>{variant.VarientName}</p>

                                        <div className="flex items-center gap-1 font-medium text-[14px] leading-[22px] text-[#666666]">
                                            <span>{variant.category === 'Beauty & Personal Care' ? 'Volume' : 'Size'}: {variant.VarientSize}</span>

                                            <span>Color: {variant.VarientColor}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="w-[18%] px-[10px] py-5 font-semibold text-base text-[#121212]">{variant.VarientQuantity}</div>
                            
                                <div className="w-[18%] px-[10px] py-[18px]">
                                    <input 
                                        className='w-full outline-none'
                                        placeholder='Eg: 2000'
                                        type="text" 
                                        id={variant.id} 
                                        name='VarientFormInput' 
                                        value={variant.VarientFormInput} 
                                        onChange={handleStockInputChange} 
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className={`flex items-center gap-3 ml-auto ${selectedProduct?.varients?.length < 4 && 'mt-auto'}`}>
                <button onClick={handleCancel} className='outline-none border border-[#D1D5DB] shadow-[0px_1px_2px_0px_#0000000D] bg-[#FFFFFF] px-5 py-3 rounded-md font-inter font-medium text-[14px] leading-5 text-[#374151]'>Cancel</button>

                <button onClick={handleUpdateStocks} className='outline-none border border-primaryColor shadow-[0px_1px_2px_0px_#0000000D] bg-primaryColor px-5 py-3 rounded-md font-inter font-medium text-[14px] leading-5 text-[#FFFFFF]'>Update stocks</button>
            </div>
        </div>
    );
}

export default UpdateStockForm;
